import React, { useState, useEffect, useCallback } from 'react';
import {
  Wrench,
  Plus,
  Edit2,
  Trash2,
  AlertCircle,
  Home,
  Building2,
  UtensilsCrossed,
  FolderKanban,
  Sparkles,
  Tv,
  Paintbrush,
  Layers,
  ShieldCheck,
  Compass,
  Ruler,
  Lightbulb,
  Cpu,
  Armchair,
} from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Badge from '../components/Badge';
import { useToast } from '../components/Toast';
import { queries } from '../../lib/supabase/queries';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export const iconOptions = [
  { name: 'Home', component: Home, label: 'Residential Home' },
  { name: 'Building2', component: Building2, label: 'Commercial Office' },
  { name: 'Wrench', component: Wrench, label: 'Turnkey Renovation' },
  { name: 'UtensilsCrossed', component: UtensilsCrossed, label: 'Modular Kitchen' },
  { name: 'FolderKanban', component: FolderKanban, label: 'Luxury Wardrobe' },
  { name: 'Sparkles', component: Sparkles, label: 'Pooja Mandir' },
  { name: 'Tv', component: Tv, label: 'TV Unit & Media Wall' },
  { name: 'Paintbrush', component: Paintbrush, label: 'Interior Styling' },
  { name: 'Layers', component: Layers, label: '3D Architectural Plan' },
  { name: 'Compass', component: Compass, label: 'Space Planning' },
  { name: 'Ruler', component: Ruler, label: 'Custom Joinery' },
  { name: 'Armchair', component: Armchair, label: 'Furniture & Decor' },
  { name: 'Cpu', component: Cpu, label: 'Smart Automation' },
  { name: 'Lightbulb', component: Lightbulb, label: 'Ambient Lighting' },
  { name: 'ShieldCheck', component: ShieldCheck, label: 'Quality Guarantee' },
];

export function getServiceIcon(iconName, size = 18, className = '') {
  const match = iconOptions.find((i) => i.name === iconName);
  const IconComp = match ? match.component : Wrench;
  return <IconComp size={size} className={className} />;
}

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'Home',
    status: 'active',
    sort_order: 0,
  });
  const [formSaving, setFormSaving] = useState(false);

  const toast = useToast();

  const loadServices = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Supabase connection is not configured in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.services.getAll();
      setServices(data);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err.message || 'Failed to fetch services from Supabase.');
      toast.error('Failed to load services: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleOpenCreate = () => {
    setIsEditing(false);
    setSelectedService(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: 'Home',
      status: 'active',
      sort_order: services.length + 1,
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (service) => {
    setIsEditing(true);
    setSelectedService(service);
    setFormData({
      name: service.name,
      slug: service.slug,
      description: service.description || '',
      icon: service.icon || 'Home',
      status: service.status || 'active',
      sort_order: service.sort_order || 0,
    });
    setModalOpen(true);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    if (!isEditing || !formData.slug) {
      const autoSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, name, slug: autoSlug }));
    } else {
      setFormData((prev) => ({ ...prev, name }));
    }
  };

  // Quick 1-click status toggle
  const handleToggleStatus = async (service, e) => {
    e.stopPropagation();
    try {
      await queries.services.toggleStatus(service.id, service.status);
      toast.success(
        `Service status changed to ${service.status === 'active' ? 'Inactive' : 'Active'}`
      );
      loadServices();
    } catch (err) {
      toast.error('Failed to toggle status: ' + err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Service name is required');
      return;
    }

    setFormSaving(true);
    try {
      const payload = {
        ...formData,
        slug: formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      };

      if (isEditing && selectedService) {
        await queries.services.update(selectedService.id, payload);
        toast.success('Service updated in Supabase');
      } else {
        await queries.services.create(payload);
        toast.success('Service created in Supabase');
      }

      setModalOpen(false);
      loadServices();
    } catch (err) {
      console.error('Error saving service:', err);
      toast.error('Failed to save service: ' + err.message);
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;
    try {
      await queries.services.delete(selectedService.id);
      toast.success('Service deleted from Supabase');
      setConfirmOpen(false);
      setSelectedService(null);
      loadServices();
    } catch (err) {
      console.error('Error deleting service:', err);
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const columns = [
    {
      header: 'Service Name & Icon',
      accessor: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#9BB7A3]/10 text-[#9BB7A3] border border-[#9BB7A3]/20 flex items-center justify-center shrink-0">
            {getServiceIcon(row.icon, 20)}
          </div>
          <div>
            <div className="font-bold text-sm text-slate-100">{row.name}</div>
            <div className="text-xs text-slate-400 font-mono">{row.slug}</div>
          </div>
        </div>
      ),
    },
    {
      header: 'Description',
      accessor: (row) => (
        <p className="text-xs text-slate-300 line-clamp-2 max-w-md">
          {row.description || 'No description provided.'}
        </p>
      ),
    },
    {
      header: 'Status (Click to Toggle)',
      accessor: (row) => (
        <button
          onClick={(e) => handleToggleStatus(row, e)}
          className="group focus:outline-none transition active:scale-95"
          title="Click to toggle Active / Inactive"
        >
          <Badge variant={row.status === 'active' ? 'success' : 'default'}>
            {row.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        </button>
      ),
    },
    {
      header: 'Sort Order',
      accessor: (row) => (
        <span className="font-mono text-xs text-slate-400">#{row.sort_order || 0}</span>
      ),
    },
    {
      header: 'Actions',
      className: 'text-right',
      accessor: (row) => (
        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleOpenEdit(row)}
            className="p-2 rounded-xl text-slate-400 hover:text-[#9BB7A3] hover:bg-[#9BB7A3]/10 transition"
            title="Edit Service"
          >
            <Edit2 size={15} />
          </button>
          <button
            onClick={() => {
              setSelectedService(row);
              setConfirmOpen(true);
            }}
            className="p-2 rounded-xl text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 transition"
            title="Delete Service"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <Wrench size={24} className="text-[#9BB7A3]" />
            Studio Services Management
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Manage architectural and interior design offerings displayed on the public navigation and home page.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 active:scale-95 shrink-0"
        >
          <Plus size={16} />
          <span>Add Service</span>
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Main DataTable */}
      <DataTable
        columns={columns}
        data={services}
        loading={loading}
        searchKey="name"
        searchPlaceholder="Search services by title..."
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: [
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ],
          },
        ]}
        emptyIcon={<Wrench size={40} className="text-slate-600" />}
        emptyTitle="No Services Found"
        emptyDescription="Create your first studio service offering to display on your public website."
        onAddNew={handleOpenCreate}
        addNewLabel="Add Service"
      />

      {/* Create / Edit Service Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => !formSaving && setModalOpen(false)}
        title={isEditing ? 'Edit Service' : 'Create Studio Service'}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Service Name <span className="text-rose-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={handleNameChange}
                placeholder="e.g., Turnkey Home Renovation"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                URL Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="turnkey-home-renovation"
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          {/* Icon Selector Grid */}
          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Select Service Icon
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 p-3 bg-slate-950 rounded-2xl border border-slate-800 max-h-48 overflow-y-auto">
              {iconOptions.map((opt) => {
                const IconComp = opt.component;
                const isSelected = formData.icon === opt.name;
                return (
                  <button
                    key={opt.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: opt.name })}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-xl border transition ${
                      isSelected
                        ? 'bg-[#9BB7A3]/20 border-[#9BB7A3] text-[#9BB7A3] font-bold'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                    }`}
                  >
                    <IconComp size={20} className="mb-1" />
                    <span className="text-[10px] truncate max-w-full">{opt.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                <option value="active">Active (Visible)</option>
                <option value="inactive">Inactive (Hidden)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({ ...formData, sort_order: parseInt(e.target.value, 10) || 0 })
                }
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
              Service Description
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the service scope, materials, craftsmanship, and deliverables..."
              className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formSaving}
              className="px-6 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 disabled:opacity-50"
            >
              {formSaving ? 'Saving to Database...' : isEditing ? 'Update Service' : 'Create Service'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedService(null);
        }}
        onConfirm={handleDelete}
        title="Delete Service"
        message={`Are you sure you want to delete service "${selectedService?.name}"?`}
        confirmText="Delete Service"
        type="danger"
      />
    </div>
  );
}
